"use client"

import clsx from "clsx"
import * as React from "react"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseFormElementProps } from "../../utils"
import { TIcon } from "../display/TIcon"

/**
 * @description 호버 시 편집 가능한 입력 필드. 평소에는 텍스트처럼 보이다가 hover/focus 시 입력 필드로 변환
 * @example
 * <THoverInput value={title} placeholder="제목 입력" onChange={setTitle} />
 */
interface THoverInputProps extends Omit<BaseFormElementProps, "required" | "readOnly"> {
	/** 현재 텍스트 값 */
	value?: string
	/** 입력 필드 name 속성 */
	name?: string
	/** 플레이스홀더 텍스트 */
	placeholder?: string
	/** 입력 필드 앞에 표시할 콘텐츠 (아이콘 등) */
	prefix?: React.ReactNode
	/** 마운트 시 자동 포커스 */
	autoFocus?: boolean
	/** 최대 입력 글자 수 */
	maxLength?: number
	/** 값 변경 콜백. blur 시 변경된 값이 전달됨 */
	onChange: (value: string) => void
	/** 포커스 해제 콜백 */
	onBlur?: () => void
}

function THoverInputInner(props: THoverInputProps) {
	const {
		id,
		name,
		className,
		value = "",
		placeholder,
		prefix,
		disabled = false,
		error = false,
		autoFocus = false,
		maxLength,
		dataAttributes,
		onChange,
		onBlur,
		...restProps
	} = props

	const inputRef = useRef<HTMLInputElement>(null)
	const [internalValue, setInternalValue] = useState<string>(value)
	const [isFocused, setIsFocused] = useState<boolean>(false)
	const dataProps = useDataAttributes(dataAttributes)

	useEffect(() => {
		setInternalValue(value)
	}, [value])

	useEffect(() => {
		if (autoFocus && inputRef.current) {
			inputRef.current.focus()
		}
	}, [autoFocus])

	const handleSaveChange = useCallback(() => {
		if (value !== internalValue) {
			onChange(internalValue)
		}
		setInternalValue(value)
		setIsFocused(false)
	}, [value, internalValue, onChange])

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = maxLength ? event.target.value.slice(0, maxLength) : event.target.value
			setInternalValue(newValue)
		},
		[maxLength]
	)

	const handleFocus = useCallback(() => {
		setIsFocused(true)
	}, [])

	const handleBlur = useCallback(() => {
		handleSaveChange()
		onBlur?.()
	}, [handleSaveChange, onBlur])

	const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			inputRef.current?.blur()
		}
	}, [])

	const iconColor = React.useMemo(() => {
		if (error) return "error"
		if (isFocused) return "primary"
		return "text"
	}, [error, isFocused])

	return (
		<div className={clsx("THoverInput", className)} {...restProps} data-focused={isFocused} data-disabled={disabled} data-invalid={error} {...dataProps}>
			{prefix && <div className="prefix">{prefix}</div>}

			<input
				ref={inputRef}
				id={id}
				name={name}
				type="text"
				value={internalValue}
				placeholder={placeholder}
				disabled={disabled}
				autoFocus={autoFocus}
				maxLength={maxLength}
				aria-invalid={error}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
			/>

			<div className="suffix">
				<TIcon name="pen" color={iconColor} size={16} />
			</div>
		</div>
	)
}

THoverInputInner.displayName = "THoverInput"

export const THoverInput = memo(THoverInputInner)
